/**
 * Client-side document I/O for Fruit Slices.
 *
 * These back the `document.upload` / `document.download` capabilities. They run
 * entirely in the Grove client (the host window, not the sandboxed iframe) and
 * never touch the backend — nothing is persisted. Upload reads a user-picked
 * file into memory and hands the bytes to the slice; download turns
 * slice-composed bytes into a browser save.
 */

export interface PickedDocument {
    name: string;
    contentType: string;
    size: number;
    dataBase64: string;
}

export interface DownloadDocumentInput {
    name: string;
    dataBase64: string;
    contentType?: string;
}

/** Strip the `data:<mime>;base64,` prefix from a FileReader data URL. */
const base64FromDataUrl = (dataUrl: string): string => {
    const commaIndex = dataUrl.indexOf(',');
    return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
};

/**
 * Open a native file picker and resolve with the chosen file's bytes (base64).
 * Resolves with `null` if the user dismisses the dialog without choosing a file.
 */
export const pickAndReadDocument = async (
    accept?: string,
): Promise<PickedDocument | null> => {
    return await new Promise<PickedDocument | null>((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        if (accept != null && accept !== '') {
            input.accept = accept;
        }
        input.style.display = 'none';

        let settled = false;
        const cleanup = () => {
            window.removeEventListener('focus', handleCancel);
            input.remove();
        };

        // A window `focus` after the picker closes with no file means "cancel".
        const handleCancel = () => {
            // Defer so a `change` event (if any) wins the race and settles first.
            setTimeout(() => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                resolve(null);
            }, 300);
        };

        input.addEventListener('change', () => {
            const file = input.files?.[0];
            if (file == null) {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                resolve(null);
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                const dataUrl =
                    typeof reader.result === 'string' ? reader.result : '';
                resolve({
                    name: file.name,
                    contentType: file.type || 'application/octet-stream',
                    size: file.size,
                    dataBase64: base64FromDataUrl(dataUrl),
                });
            };
            reader.onerror = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                reject(
                    reader.error ??
                        new Error('Failed to read the selected file'),
                );
            };
            reader.readAsDataURL(file);
        });

        window.addEventListener('focus', handleCancel);
        document.body.appendChild(input);
        input.click();
    });
};

/** Turn base64 bytes into a Blob and trigger a browser download. */
export const downloadDocument = async ({
    name,
    dataBase64,
    contentType,
}: DownloadDocumentInput): Promise<void> => {
    const binary = atob(dataBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], {
        type: contentType ?? 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Revoke on the next tick so the download has a chance to start.
    await new Promise<void>((resolve) => {
        setTimeout(() => {
            URL.revokeObjectURL(url);
            resolve();
        }, 0);
    });
};
